// Shared types + display helpers for the Owners module.
import { formatDate, initials } from './interviews'

export { formatDate, initials }

export type OwnerRow = {
  application_id: string
  person_id: string | null
  full_name: string | null
  primary_email: string | null
  primary_phone: string | null
  owner_model: string | null
  current_status: string
  score: number | null
  submitted_at: string | null
  cars_requested: string | null
  owns_vehicle: boolean | null
  vehicle_make: string | null
  last_contact_at: string | null
  follow_up_at: string | null
  waitlist_at: string | null
  notes: string | null
  // From owner_board_v2 (getOwnerBoard). Absent on the older getOwnerPipeline rows.
  application_status?: string | null
  vehicle_model?: string | null
  vehicle_year?: number | null
  in_stage_since?: string | null
  days_in_stage?: number | null
  meeting_at?: string | null
  live_vehicle_count?: number | null
  live_plates?: string | null
  exited_vehicle_count?: number | null
  all_live_plates?: string | null
  all_live_vehicle_count?: number | null
  application_count?: number | null
  other_applications?: { model: string; status: string; reason: string | null }[] | null
}

export type StatusOption = { code: string; label: string }

// Every owner pipeline stage with a plain-language label, in workflow order.
export const OWNER_STATUS_OPTIONS: StatusOption[] = [
  { code: 'submitted', label: 'Submitted' },
  { code: 'not_contacted', label: 'Not contacted' },
  { code: 'waitlist', label: 'Waitlist' },
  { code: 'responded', label: 'Responded' },
  { code: 'meeting_booked', label: 'Meeting booked' },
  { code: 'meeting_unsuccessful', label: 'Meeting unsuccessful' },
  { code: 'sourcing_vehicle', label: 'Sourcing vehicle' },
  { code: 'vehicle_assessment', label: 'Vehicle assessment' },
  { code: 'documents_pending', label: 'Documents pending' },
  { code: 'finance_consent_pending', label: 'Finance house consent' },
  { code: 'uber_approval_pending', label: 'Uber approval pending' },
  { code: 'uber_rejected', label: 'Uber rejected' },
  { code: 'contracting', label: 'Contracting' },
  { code: 'contract_pending', label: 'Contract pending' },
  { code: 'onboarding', label: 'Onboarding' },
  { code: 'onboarded', label: 'Onboarded' },
  { code: 'offboarding', label: 'Offboarding' },
  { code: 'offboarded', label: 'Offboarded' },
  { code: 'not_qualified', label: 'Not qualified' },
]

const STATUS_LABELS: Record<string, string> = Object.fromEntries(
  OWNER_STATUS_OPTIONS.map((o) => [o.code, o.label]),
)

export function ownerStatusLabel(code: string | null | undefined): string {
  if (!code) return 'Unknown'
  if (STATUS_LABELS[code]) return STATUS_LABELS[code]!
  return code
    .split('_')
    .map((w) => (w.length ? w[0]!.toUpperCase() + w.slice(1) : w))
    .join(' ')
}

export type StatusTone = 'brand' | 'amber' | 'green' | 'red' | 'neutral'

const STATUS_TONES: Record<string, StatusTone> = {
  onboarded: 'green',
  not_qualified: 'red',
  offboarded: 'red',
  uber_rejected: 'red',
  meeting_unsuccessful: 'red',
  meeting_booked: 'brand',
  responded: 'brand',
  contracting: 'brand',
  contract_pending: 'brand',
  onboarding: 'brand',
  waitlist: 'amber',
  sourcing_vehicle: 'amber',
  vehicle_assessment: 'amber',
  finance_consent_pending: 'amber',
  documents_pending: 'amber',
  uber_approval_pending: 'amber',
  submitted: 'neutral',
  not_contacted: 'neutral',
  offboarding: 'neutral',
}

export function ownerStatusTone(code: string | null | undefined): StatusTone {
  if (!code) return 'neutral'
  return STATUS_TONES[code] ?? 'neutral'
}

// The three owner tracks. A person shows on every track they are in (0287);
// which application represents them on each is decided in v_owner_track_position.
export const MODEL_LABELS: Record<string, string> = {
  asset_finance: 'Cash for car',
  brought_in: 'Car to new model',
  brings_own_vehicle: 'Old model',
}

// Owner tracks offered when editing an owner.
export const OWNER_MODEL_OPTIONS: StatusOption[] = [
  { code: 'asset_finance', label: 'Cash for car' },
  { code: 'brought_in', label: 'Car to new model' },
  { code: 'brings_own_vehicle', label: 'Old model' },
]

export function ownerModelLabel(code: string | null | undefined): string {
  if (!code) return 'Unknown'
  if (MODEL_LABELS[code]) return MODEL_LABELS[code]!
  return code
    .split('_')
    .map((w) => (w.length ? w[0]!.toUpperCase() + w.slice(1) : w))
    .join(' ')
}
